import React, { useEffect, memo } from "react";
import { Mic } from "lucide-react";
import { motion } from "framer-motion";

interface VoiceCommandFeedbackProps {
  command: string;
  isVisible: boolean;
  onHide: () => void;
}

// Use React.memo to prevent unnecessary re-renders
const VoiceCommandFeedback = memo(function VoiceCommandFeedback({
  command,
  isVisible,
  onHide,
}: VoiceCommandFeedbackProps) {
  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;

    if (isVisible) {
      // Clear any existing timer
      if (timer) {
        clearTimeout(timer);
      }

      // Set new timer
      timer = setTimeout(() => {
        onHide();
      }, 2000);

      return () => {
        if (timer) {
          clearTimeout(timer);
        }
      };
    }
  }, [isVisible, onHide]);

  if (!isVisible) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="fixed bottom-20 left-1/2 transform -translate-x-1/2 bg-primary text-primary-foreground px-4 py-2 rounded-full shadow-lg z-50"
    >
      <div className="flex items-center space-x-2">
        <div className="bg-primary-foreground/20 rounded-full p-1">
          <Mic className="h-4 w-4" />
        </div>
        <span className="text-sm font-medium">{command}</span>
      </div>
    </motion.div>
  );
});

export default VoiceCommandFeedback;
